// React and related imports
import React, { useMemo, useState } from 'react';

// i18 hooks
import { useTranslation } from 'react-i18next';

// Flowbite components
import { Table } from 'flowbite-react';

// Types & Interfaces
import { ISessionWithStats } from '../../common/interfaces/Session.interface';

// Custom Components
import { SessionTableHead } from './SessionTableHead';
import { SessionTableBody } from './SessionTableBody';

// Utils
import { SortableKeys, sortItems } from '../../utils/sortItems';

interface SessionTableProps {
  sessions: ISessionWithStats[];
  onEdit: (session: ISessionWithStats) => void;
}

export const SessionTable: React.FC<SessionTableProps> = ({
  sessions,
  onEdit,
}) => {
  const { t } = useTranslation();

  const [sortBy, setSortBy] = useState<string>('');
  const [ascending, setAscending] = useState(true);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setAscending(!ascending); // Toggle if same field clicked again
    } else {
      setSortBy(field);
      setAscending(true); // Reset to ascending when switching fields
    }
  };

  const sortedItems = useMemo(() => {
    return sortItems(sessions ?? [], sortBy as SortableKeys, ascending);
  }, [sessions, sortBy, ascending]);

  if (sessions.length === 0) {
    return (
      <div className="py-10 text-center text-gray-500 dark:text-gray-400">
        {t('sessions.noSessions')}
      </div>
    );
  }

  return (
    <div className="w-full max-w-full overflow-x-auto">
      <Table id="sessions-table">
        <SessionTableHead sortingHandler={handleSort} />
        <SessionTableBody sessions={sortedItems} onEdit={onEdit} />
      </Table>
    </div>
  );
};
